import type { AgentRuntimeSnapshot, AgentType } from "@cord/shared"
import type { CursorRawProjection } from "@/lib/cursor-raw-runtime"
import type { OpenCodeRawProjection } from "@/lib/opencode-raw-runtime"
import type { ClaudeRawProjection } from "@/lib/claude-raw-runtime"
import { getSessionRuntimeAdapter } from "@/lib/session-runtime-adapters"
import { isSupportedSessionRuntimeAgentType } from "@/lib/session-runtime-adapters"
import { reconcileRuntimeSnapshot } from "@/lib/runtime-snapshot-stability"
import type { NormalizedRawSyncEvent } from "@/lib/runtime-sync-batch"

export type AgentRuntimeComputationState = {
  agentType: AgentType | null
  snapshot: AgentRuntimeSnapshot | null
  lastAppliedTimestamp: number | null
  lastEventId: number | null
  rawOpenCodeProjection: OpenCodeRawProjection | null
  rawClaudeProjection: ClaudeRawProjection | null
  rawCursorProjection: CursorRawProjection | null
}

export type ComputedRawSyncEventsBatch = {
  appliedCount: number
  maxEventId: number | null
  agentType: AgentType | null
  didChange: boolean
  nextState: AgentRuntimeComputationState
}

export type ComputedRawSyncEventsBatchProjectionResult =
  | {
      type: "projection_batch"
      agentType: "opencode"
      appliedCount: number
      maxEventId: number
      lastAppliedTimestamp: number
      lastEventId: number
      projection: OpenCodeRawProjection
    }
  | {
      type: "projection_batch"
      agentType: "claude_code"
      appliedCount: number
      maxEventId: number
      lastAppliedTimestamp: number
      lastEventId: number
      projection: ClaudeRawProjection
    }
  | {
      type: "projection_batch"
      agentType: "cursor"
      appliedCount: number
      maxEventId: number
      lastAppliedTimestamp: number
      lastEventId: number
      projection: CursorRawProjection
    }

function normalizeEventId(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null
  return Math.floor(value)
}

function getRawProjectionForAgent(
  state: AgentRuntimeComputationState,
  agentType: AgentType,
): OpenCodeRawProjection | ClaudeRawProjection | CursorRawProjection | null {
  if (agentType === "opencode") return state.rawOpenCodeProjection
  if (agentType === "claude_code") return state.rawClaudeProjection
  if (agentType === "cursor") return state.rawCursorProjection
  return null
}

function buildRawProjectionState(
  agentType: AgentType,
  rawProjection: OpenCodeRawProjection | ClaudeRawProjection | CursorRawProjection,
): Pick<
  AgentRuntimeComputationState,
  "rawOpenCodeProjection" | "rawClaudeProjection" | "rawCursorProjection"
> {
  if (agentType === "opencode") {
    return {
      rawOpenCodeProjection: rawProjection as OpenCodeRawProjection,
      rawClaudeProjection: null,
      rawCursorProjection: null,
    }
  }

  if (agentType === "claude_code") {
    return {
      rawOpenCodeProjection: null,
      rawClaudeProjection: rawProjection as ClaudeRawProjection,
      rawCursorProjection: null,
    }
  }

  return {
    rawOpenCodeProjection: null,
    rawClaudeProjection: null,
    rawCursorProjection: rawProjection as CursorRawProjection,
  }
}

function normalizeRawSyncEvent(input: {
  eventType: string
  payload: unknown
  eventId: number
  childSessionId?: string | null
  timestamp?: number
}): NormalizedRawSyncEvent | null {
  const eventId = normalizeEventId(input.eventId)
  if (eventId === null) return null

  const normalizedTimestamp =
    typeof input.timestamp === "number" && Number.isFinite(input.timestamp)
      ? Math.floor(input.timestamp)
      : Date.now()
  const childSessionId =
    typeof input.childSessionId === "string" && input.childSessionId.trim().length > 0
      ? input.childSessionId.trim()
      : null

  return {
    eventType: input.eventType,
    payload: input.payload,
    eventId,
    childSessionId,
    timestamp: normalizedTimestamp,
  }
}

function promoteSnapshotProviderEventId(
  snapshot: AgentRuntimeSnapshot | null,
  providerEventId: number | null,
): AgentRuntimeSnapshot | null {
  if (!snapshot || providerEventId === null) return snapshot
  if (snapshot.cursorState.providerEventId === providerEventId) return snapshot

  return {
    ...snapshot,
    cursorState: {
      ...snapshot.cursorState,
      providerEventId,
    },
  }
}

export function buildNoChangeComputedRawSyncEventsBatch(args: {
  currentState: AgentRuntimeComputationState
  maxEventId: number | null
}): ComputedRawSyncEventsBatch {
  return {
    appliedCount: 0,
    maxEventId: args.maxEventId,
    agentType: args.currentState.agentType,
    didChange: false,
    nextState: args.currentState,
  }
}

export function buildCursorOnlyComputedRawSyncEventsBatch(args: {
  currentState: AgentRuntimeComputationState
  maxEventId: number
}): ComputedRawSyncEventsBatch {
  const advancedSnapshot = promoteSnapshotProviderEventId(args.currentState.snapshot, args.maxEventId)
  return {
    appliedCount: 0,
    maxEventId: args.maxEventId,
    agentType: args.currentState.agentType,
    didChange: (args.currentState.lastEventId ?? 0) < args.maxEventId,
    nextState: {
      ...args.currentState,
      snapshot:
        advancedSnapshot !== null
          ? reconcileRuntimeSnapshot(args.currentState.snapshot, advancedSnapshot)
          : args.currentState.snapshot,
      lastEventId: args.maxEventId,
    },
  }
}

export function buildComputedRawSyncEventsBatchProjectionResult(args: {
  computed: ComputedRawSyncEventsBatch
}): ComputedRawSyncEventsBatchProjectionResult | null {
  if (
    !args.computed.didChange
    || args.computed.appliedCount <= 0
    || !isSupportedSessionRuntimeAgentType(args.computed.agentType)
    || args.computed.maxEventId === null
    || args.computed.nextState.lastAppliedTimestamp === null
    || args.computed.nextState.lastEventId === null
  ) {
    return null
  }

  if (args.computed.agentType === "opencode" && args.computed.nextState.rawOpenCodeProjection !== null) {
    return {
      type: "projection_batch",
      agentType: "opencode",
      appliedCount: args.computed.appliedCount,
      maxEventId: args.computed.maxEventId,
      lastAppliedTimestamp: args.computed.nextState.lastAppliedTimestamp,
      lastEventId: args.computed.nextState.lastEventId,
      projection: args.computed.nextState.rawOpenCodeProjection,
    }
  }

  if (args.computed.agentType === "claude_code" && args.computed.nextState.rawClaudeProjection !== null) {
    return {
      type: "projection_batch",
      agentType: "claude_code",
      appliedCount: args.computed.appliedCount,
      maxEventId: args.computed.maxEventId,
      lastAppliedTimestamp: args.computed.nextState.lastAppliedTimestamp,
      lastEventId: args.computed.nextState.lastEventId,
      projection: args.computed.nextState.rawClaudeProjection,
    }
  }

  if (args.computed.agentType === "cursor" && args.computed.nextState.rawCursorProjection !== null) {
    return {
      type: "projection_batch",
      agentType: "cursor",
      appliedCount: args.computed.appliedCount,
      maxEventId: args.computed.maxEventId,
      lastAppliedTimestamp: args.computed.nextState.lastAppliedTimestamp,
      lastEventId: args.computed.nextState.lastEventId,
      projection: args.computed.nextState.rawCursorProjection,
    }
  }

  return null
}

export function buildComputedRawSyncEventsBatchFromProjectionResult(args: {
  currentState: AgentRuntimeComputationState
  result: ComputedRawSyncEventsBatchProjectionResult
}): ComputedRawSyncEventsBatch {
  const adapter = getSessionRuntimeAdapter(args.result.agentType)
  if (!adapter) {
    throw new Error("missing runtime adapter for projection batch result")
  }
  if (!adapter.isRawProjectionLike(args.result.projection)) {
    throw new Error("invalid projection batch result")
  }

  const previousSnapshot =
    args.currentState.agentType === args.result.agentType ? args.currentState.snapshot : null
  const nextSnapshot = promoteSnapshotProviderEventId(
    adapter.buildSnapshot(args.result.projection, args.result.lastEventId),
    args.result.maxEventId,
  )
  if (!nextSnapshot) {
    throw new Error("expected projection batch snapshot")
  }

  return {
    appliedCount: args.result.appliedCount,
    maxEventId: args.result.maxEventId,
    agentType: args.result.agentType,
    didChange: true,
    nextState: {
      agentType: args.result.agentType,
      snapshot: reconcileRuntimeSnapshot(previousSnapshot, nextSnapshot),
      ...buildRawProjectionState(args.result.agentType, args.result.projection),
      lastAppliedTimestamp: args.result.lastAppliedTimestamp,
      lastEventId: args.result.lastEventId,
    },
  }
}

function applyBatchForAgent(
  agentType: AgentType,
  state: AgentRuntimeComputationState,
  events: NormalizedRawSyncEvent[],
) {
  if (events.length === 0) return null
  const adapter = getSessionRuntimeAdapter(agentType)
  if (!adapter) return null

  const applied = adapter.applyBatch({
    projection: getRawProjectionForAgent(state, agentType) as never,
    events,
  })
  if (!applied) return null

  return {
    agentType: adapter.agentType,
    snapshot: applied.snapshot,
    ...buildRawProjectionState(
      adapter.agentType,
      applied.projection as OpenCodeRawProjection | ClaudeRawProjection | CursorRawProjection,
    ),
    lastAppliedTimestamp: applied.lastAppliedTimestamp,
    lastEventId: applied.lastAppliedEventId,
    appliedCount: applied.appliedCount,
  }
}

export function computeRawSyncEventsBatch(args: {
  currentState: AgentRuntimeComputationState
  events: Array<{
    eventType: string
    payload: unknown
    eventId: number
    childSessionId?: string | null
    timestamp?: number
  }>
}): ComputedRawSyncEventsBatch {
  if (!Array.isArray(args.events) || args.events.length === 0) {
    return buildNoChangeComputedRawSyncEventsBatch({
      currentState: args.currentState,
      maxEventId: null,
    })
  }

  const state = args.currentState
  const hasProjection =
    state.rawOpenCodeProjection !== null ||
    state.rawClaudeProjection !== null ||
    state.rawCursorProjection !== null
  const currentEventId = state.lastEventId
  let lastAcceptedEventId = hasProjection ? currentEventId : null
  const normalizedEvents: NormalizedRawSyncEvent[] = []
  for (const event of args.events) {
    const normalized = normalizeRawSyncEvent(event)
    if (!normalized) continue
    if (lastAcceptedEventId !== null && normalized.eventId <= lastAcceptedEventId) continue
    normalizedEvents.push(normalized)
    lastAcceptedEventId = normalized.eventId
  }

  if (normalizedEvents.length === 0) {
    return buildNoChangeComputedRawSyncEventsBatch({
      currentState: state,
      maxEventId: null,
    })
  }

  const maxEventId = normalizedEvents.reduce<number | null>(
    (max, event) => Math.max(max ?? 0, event.eventId),
    null,
  )

  const attemptedAgentOrderSource: AgentType[] = state.agentType
    ? [state.agentType, "cursor", "claude_code", "opencode"]
    : ["cursor", "claude_code", "opencode"]
  const attemptedAgentOrder = attemptedAgentOrderSource.filter(
    (agentType, index, values) => values.indexOf(agentType) === index,
  )

  for (const agentType of attemptedAgentOrder) {
    const applied = applyBatchForAgent(agentType, state, normalizedEvents)
    if (!applied) continue
    const previousSnapshot = state.agentType === applied.agentType ? state.snapshot : null
    const committedEventId = maxEventId ?? applied.lastEventId
    const nextSnapshot = promoteSnapshotProviderEventId(applied.snapshot, committedEventId)
    const stabilizedSnapshot = nextSnapshot
      ? reconcileRuntimeSnapshot(previousSnapshot, nextSnapshot)
      : previousSnapshot

    return {
      appliedCount: applied.appliedCount,
      maxEventId,
      agentType: applied.agentType,
      didChange: true,
      nextState: {
        agentType: applied.agentType,
        snapshot: stabilizedSnapshot,
        rawOpenCodeProjection: applied.rawOpenCodeProjection,
        rawClaudeProjection: applied.rawClaudeProjection,
        rawCursorProjection: applied.rawCursorProjection,
        lastAppliedTimestamp: applied.lastAppliedTimestamp,
        lastEventId: committedEventId,
      },
    }
  }

  if (maxEventId !== null && (state.lastEventId ?? 0) < maxEventId) {
    return buildCursorOnlyComputedRawSyncEventsBatch({
      currentState: state,
      maxEventId,
    })
  }

  return buildNoChangeComputedRawSyncEventsBatch({
    currentState: state,
    maxEventId,
  })
}
